// Centralized API utility for all API calls

const GEOLOCATION_API = 'https://validationapi.thetechmenders.com/api/GeoLocation/GetGeoLocation';
const LEAD_API = 'https://leadapi.crmbytes.com/api/Lead/AddLead';

// Country code to dial code mapping
const countryToDialCode = {
  'US': 1, 'CA': 1, 'GB': 44, 'UK': 44, 'IN': 91, 'AU': 61, 'CN': 86,
  'JP': 81, 'DE': 49, 'FR': 33, 'IT': 39, 'ES': 34, 'MX': 52, 'BR': 55,
  'RU': 7, 'ZA': 27, 'AE': 971, 'PK': 92, 'NL': 31, 'SE': 46, 'NO': 47,
  'DK': 45, 'FI': 358, 'PL': 48, 'TR': 90, 'SA': 966, 'EG': 20, 'NG': 234
};

/**
 * Fetch user's geolocation data
 * @returns {Promise<Object>} Geolocation data including country, IP, city, etc.
 */
export const getGeoLocation = async () => {
  try {
    const response = await fetch(GEOLOCATION_API, {
      method: 'GET',
      headers: {
        'accept': 'text/plain'
      }
    });
    
    const data = await response.json();
    
    if (data.isSuccess && data.statusCode === 200) {
      return {
        success: true,
        data: data.data,
        countryCode: data.data.countryCode,
        countryName: data.data.countryName,
        ipAddress: data.data.ipAddress,
        city: data.data.cityName || '',
        state: data.data.regionName || '',
        dialCode: countryToDialCode[data.data.countryCode] || 1,
        fullResponse: data
      };
    }
    
    return {
      success: false,
      error: 'Failed to fetch geolocation',
      dialCode: 1,
      countryCode: 'US'
    };
  } catch (error) {
    console.error('Geolocation API error:', error);
    return {
      success: false,
      error: error.message,
      dialCode: 1,
      countryCode: 'US'
    };
  }
};

/**
 * Submit lead form data
 * @param {Object} formData - Form data to submit
 * @param {string} formData.name - Customer name
 * @param {string} formData.email - Customer email
 * @param {string} formData.phoneNumber - Phone number (numeric only)
 * @param {number} formData.countryDialCode - Country dial code
 * @param {string} formData.country - Country code (e.g., 'US')
 * @param {string} formData.description - Message/description
 * @param {string} formData.ipAddress - IP address from geolocation
 * @param {string} formData.pageUrl - Current page URL
 * @param {string} formData.city - City (optional)
 * @param {string} formData.state - State (optional)
 * @param {Object} formData.geoLocation - Full geolocation response (optional)
 * @param {string} formData.packageTitle - Package name (optional)
 * @param {number} formData.packageAmount - Package amount (optional)
 * @returns {Promise<Object>} API response with success/error status
 */
export const submitLead = async (formData) => {
  try {
    // Ensure phone number is numeric only
    const cleanPhoneNumber = formData.phoneNumber.replace(/\D/g, '');
    
    const payload = {
      name: formData.name,
      email: formData.email,
      countryDialCode: parseInt(formData.countryDialCode),
      country: formData.country || 'US',
      phoneNumber: cleanPhoneNumber,
      ipAddress: formData.ipAddress || '',
      description: formData.description || '',
      pageUrl: formData.pageUrl || window.location.href,
      city: formData.city || '',
      state: formData.state || '',
      geoLocation: formData.geoLocation ? JSON.stringify(formData.geoLocation) : '',
      packageTitle: formData.packageTitle || '',
      packageAmount: formData.packageAmount || 0
    };

    const response = await fetch(LEAD_API, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'accept': 'text/plain'
      },
      body: JSON.stringify(payload)
    });

    const data = await response.json();

    // Handle success (200)
    if (data.statusCode === 200 && data.isSuccess) {
      return {
        success: true,
        customerId: data.data.customerId,
        clientId: data.data.clientId,
        message: data.data.message,
        data: data.data
      };
    }

    // Handle 401 Unauthorized
    if (data.statusCode === 401) {
      return {
        success: false,
        error: data.message || 'Unauthorized',
        statusCode: 401
      };
    }

    // Handle 400 Bad Request
    if (data.statusCode === 400) {
      return {
        success: false,
        error: data.errorMessage || data.data?.message || 'Invalid data provided',
        statusCode: 400
      };
    }

    // Handle other errors
    return {
      success: false,
      error: data.errorMessage || 'An error occurred',
      statusCode: data.statusCode
    };

  } catch (error) {
    console.error('Lead API error:', error);
    return {
      success: false,
      error: 'Network error. Please try again.',
      statusCode: 500
    };
  }
};

/**
 * Get dial code from country code
 * @param {string} countryCode - Two-letter country code
 * @returns {number} Dial code
 */
export const getDialCodeFromCountry = (countryCode) => {
  return countryToDialCode[countryCode] || 1;
};
